import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronDown } from '#src/atoms/icons/system/iconChevronDown.ts';
import { Collapse } from '#src/atoms/layout/Collapse/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LinkBox, LinkOverlay } from '#src/atoms/navigation/Box/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

export type AccordionChildren =
  | ReactNode
  | ((props: { expanded: boolean }) => ReactNode);

type AccordionContextValue = {
  expanded: boolean;
  toggle: () => void;
  titleId: string;
};

const AccordionContext = createContext<AccordionContextValue | null>(null);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(
      'Accordion.Title/Details must be used within Accordion.Wrapper'
    );
  }
  return context;
};

type WrapperProps = {
  children: ReactNode;
  // `expanded` => controlled; omit it and use `defaultExpanded` => uncontrolled.
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  className?: string;
};

const Wrapper = ({
  children,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandChange,
  className,
}: WrapperProps) => {
  const titleId = useId();
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const toggle = useCallback(() => {
    if (!isControlled) {
      setUncontrolledExpanded(prev => !prev);
    }
    onExpandChange?.(!expanded);
  }, [isControlled, onExpandChange, expanded]);

  const context = useMemo(
    () => ({ expanded, toggle, titleId }),
    [expanded, toggle, titleId]
  );

  return (
    <AccordionContext.Provider value={context}>
      <Vertical
        gap={0}
        className={className}
        data-state={expanded ? 'open' : 'closed'}
      >
        {children}
      </Vertical>
    </AccordionContext.Provider>
  );
};

const Chevron = styled(FontAwesomeIcon)`
  color: var(--c-nd-600-opaque);
  fill: var(--c-nd-600);
  transform: rotate(0deg);
  transition: transform 0.3s;
  [data-state='open'] & {
    transform: rotate(180deg);
  }
`;

const TitleRow = styled(LinkBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

type TitleProps = {
  children: ReactNode;
  showChevron?: boolean;
  className?: string;
};

const Title = ({ children, showChevron = true, className }: TitleProps) => {
  const { formatMessage } = useIntl();
  const { expanded, toggle, titleId } = useAccordionContext();

  return (
    <TitleRow id={titleId} className={className}>
      {children}
      <LinkOverlay
        as="button"
        aria-labelledby={titleId}
        aria-expanded={expanded}
        onClick={toggle}
        style={{
          display: showChevron ? undefined : 'contents',
        }}
      >
        {!!showChevron && (
          <Chevron
            icon={iconChevronDown}
            height={16}
            aria-label={formatMessage(
              expanded ? glossary.showLess : glossary.showMore
            )}
          />
        )}
      </LinkOverlay>
    </TitleRow>
  );
};

type DetailsProps = {
  children: AccordionChildren;
  className?: string;
};

const Details = ({ children, className }: DetailsProps) => {
  const { expanded } = useAccordionContext();

  return (
    <Collapse open={expanded} gap={0}>
      <div className={className}>
        {typeof children === 'function' ? children({ expanded }) : children}
      </div>
    </Collapse>
  );
};

export const Accordion = { Wrapper, Title, Details };
